import { IBusinessResponseListDto } from '@/models/business';
import {
  IPaginationResponseDto,
  IQueryPaginationOptions
} from '@/models/general';
import { FormManuallyReward, FormWebhookFakeFidel } from '@/models/transaction';
import APIService from '../api';
import { ICustomerResponseListDto } from '../models/customer';
import { CreateTicketRequestDto } from './interfaces/create-ticket-request.interface';
import { UpdateTicketRequestDto } from './interfaces/update-ticket-request.interface';
import { TicketSource } from './enums/ticket-source.enum';

export default class AdminService extends APIService {
  getPayments(resource?: string) {
    return this.get(resource || '/super-admin/dashboard');
  }

  getBusinessCustomers(businessId: string, options: IQueryPaginationOptions) {
    return this.getQueryPagination(
      `/super-admin/business/${businessId}/customers`,
      options
    );
  }

  getTwismSendCoinsHistory(businessId: string, url?: string) {
    return this.get(
      url || `/super-admin/twism-wallet/${businessId}/send-coins`
    );
  }

  getTwismSendCoinsStoreHistory(businessId: string, url?: string) {
    return this.get(
      url || `/super-admin/twism-wallet/${businessId}/send-coins-store-history`
    );
  }

  getTwismCoinDealSoldHistory(businessId: string, url?: string) {
    return this.get(
      url || `/super-admin/coin-store/${businessId}/sold-history`
    );
  }

  sendTwismCoins(businessId: string, customerId: string, coins: number) {
    return this.post(`/super-admin/twism-wallet/${businessId}/send-coins`, {
      customer: customerId,
      coins
    });
  }

  sendTwismCoinsToCoinStore(businessId: string, coins: number) {
    return this.post(
      `/super-admin/twism-wallet/${businessId}/send-coins-store`,
      {
        coins
      }
    );
  }

  enableAutoSendingCoinStore(
    businessId: string,
    autoSendingCoinStore: boolean,
    autoSendingCoinStoreAmount: number
  ) {
    return this.post(
      `/super-admin/twism-wallet/${businessId}/auto-sending-coin-store`,
      {
        autoSendingCoinStore,
        autoSendingCoinStoreAmount
      }
    );
  }

  sendTwismCoinsToTwismWallet(businessId: string, coins: number) {
    return this.post(
      `/super-admin/twism-wallet/${businessId}/send-coins-wallet`,
      {
        coins
      }
    );
  }

  getOverview() {
    return this.get('/super-admin/twism-wallet/overview');
  }

  getCoinStoreOverview() {
    return this.get('/super-admin/coin-store/overview');
  }

  setStatusPayment(data: object) {
    return this.post('/super-admin/dashboard/payout', data);
  }

  setBusinessMerchantID(businessId: string, data: object) {
    return this.put(`/super-admin/business/${businessId}/merchant-id`, data);
  }

  connectFidel(businessId: string) {
    return this.put(`/super-admin/business/${businessId}/connectToFidel`);
  }

  setBusinessHideOnApp(businessId: string, shouldHideForCustomer: boolean) {
    return this.put(`/super-admin/business/${businessId}/update-hidden-flag`, {
      shouldHideForCustomer
    });
  }

  setBusinessStatus(
    businessId: string,
    data: { status: 'active' | 'inactive' | 'hidden' }
  ) {
    return this.put(`/super-admin/business/${businessId}/status`, data);
  }

  impersonate(data: { mail: string; business: string }) {
    return this.post('/auth/impersonate/enable', data);
  }

  disableImpersonate() {
    return this.post('/auth/impersonate/disable');
  }

  getCustomers(options: IQueryPaginationOptions) {
    return this.getQueryPagination(`/super-admin/customer`, options);
  }

  getKeywords(options: IQueryPaginationOptions) {
    return this.getQueryPagination(`/super-admin/keyword`, options);
  }

  getKeywordsByBusiness(businessId: string) {
    return this.get(`/super-admin/keyword/business/${businessId}`);
  }

  filterCustomerById(customerId: string) {
    return this.get('/super-admin/customer', {
      params: { customerId }
    });
  }

  searchCustomers(search: string) {
    return this.get<IPaginationResponseDto<ICustomerResponseListDto>>(
      '/super-admin/customer?limit=20&search=' + search
    );
  }

  getCustomerActivities(customerId: string, url?: string) {
    return this.get(url || `/super-admin/customer/${customerId}/activities`);
  }

  getBusinessesList(options: IQueryPaginationOptions) {
    return this.getQueryPagination(`/super-admin/c-business`, options);
  }
  getTwismWalletList(options: IQueryPaginationOptions) {
    return this.getQueryPagination(
      `/super-admin/twism-wallet/business`,
      options
    );
  }

  getExportTwismWalletList(options: IQueryPaginationOptions) {
    return this.getQueryPagination(
      `/super-admin/twism-wallet/business/export`,
      options
    );
  }

  setBillingDelayedPeriod(businessId: string, delayedPeriodEndAt: string) {
    return this.post(`/billing/subscription/${businessId}/delay-period`, {
      delayedPeriodEndAt
    });
  }

  extendBillingDelayedPeriod(businessId: string, days: number) {
    return this.post(`/billing/settings/${businessId}/delay-period`, {
      days
    });
  }

  getTicketsList(options: IQueryPaginationOptions) {
    return this.getQueryPagination(`/super-admin/ticket-request`, options);
  }

  createTicketRequest(data: CreateTicketRequestDto) {
    return this.post(`/super-admin/ticket-request`, {
      source: TicketSource.SuperAdmin,
      ...data
    });
  }

  updateTicketRequest(data: UpdateTicketRequestDto) {
    return this.put(`/super-admin/ticket-request`, data);
  }

  approveTicketRequest(data: UpdateTicketRequestDto) {
    return this.put(`/super-admin/ticket-request/approve`, data);
  }

  cancelTicketRequest(data: UpdateTicketRequestDto) {
    return this.put(`/super-admin/ticket-request/cancel`, data);
  }

  archiveTicketRequest(data: UpdateTicketRequestDto) {
    return this.put(`/super-admin/ticket-request/archive`, data);
  }

  replyTicketRequest(data: UpdateTicketRequestDto) {
    return this.put(`/super-admin/ticket-request/reply`, data);
  }

  getCoinStoreList(options: IQueryPaginationOptions) {
    return this.getQueryPagination(`/super-admin/coin-store`, options);
  }

  createCoinDeal(payload: {
    coinStoreId: string;
    coins: number;
    value: number;
    twismCoin?: number;
    merchantCoin?: number;
    dealType: string | null;
  }) {
    return this.post('/super-admin/coin-store-deal', payload);
  }

  createKeywordByBusiness(businessId: string, keywordsIds: string[]) {
    return this.post(`/super-admin/keyword/business/${businessId}`, {
      keywordsIds
    });
  }

  deleteKeywordByBusiness(businessId: string, keywordId: string) {
    return this.delete(`/super-admin/keyword/business/${businessId}`, {
      keywordId
    });
  }

  createKeywords(keywords: string[]) {
    return this.post(`/super-admin/keyword`, {
      keywords
    });
  }

  deleteKeyword(id: string) {
    return this.delete(`/super-admin/keyword/${id}`);
  }

  deleteCoinDeal(id: string) {
    return this.delete(`/super-admin/coin-store-deal/${id}`);
  }

  getBusinessList(baseUrl: string, title: string) {
    if (baseUrl) {
      return this.get(`/super-admin${baseUrl}`);
    }
    return this.get(`/super-admin/business?limit=20&skip=0&search=${title}`);
  }

  searchBusinesses(search: string, onlyBusinessOnFidel = false) {
    return this.get<IPaginationResponseDto<IBusinessResponseListDto>>(
      `/super-admin/business?limit=20&search=${search}&onlyBusinessOnFidel=${onlyBusinessOnFidel}`
    );
  }

  getProvidersTransactions(options: IQueryPaginationOptions) {
    return this.getQueryPagination(
      `/super-admin/merchant-connect/transactions`,
      options
    );
  }

  clearTransaction(transactionId: string) {
    return this.post(`/lambda-webhooks/transaction/transition`, {
      nextStatus: 'cleared',
      connectTransactionId: transactionId
    });
  }

  refundTransaction(transactionId: string, refundAmount: number) {
    return this.post(`/lambda-webhooks/transaction/refund`, {
      connectTransactionId: transactionId,
      refundAmount: refundAmount * 100
    });
  }

  getTransactionExternalData(transactionId: string) {
    return this.get(
      `/lambda-webhooks/transaction/${transactionId}/external-detail`
    );
  }

  getTransactions(
    sortField: string,
    sortOrder: string = '',
    pagination: string = '',
    filters: string,
    time: { extras?: string }
  ) {
    let resource = this.generateResource(
      '/super-admin/transaction/credit-card-transactions?',
      sortField,
      sortOrder,
      pagination,
      filters
    );

    if (!resource.includes('extras')) {
      resource += this.dynamicResource(time);
    }

    return this.get(resource);
  }

  newCampaign(data: object) {
    return this.post('/super-admin/campaign', data);
  }

  getCampaigns(pagination: string = '') {
    const resource = this.generateResource(
      '/super-admin/campaign?',
      'createdAt',
      '',
      pagination
    );

    return this.get(resource);
  }

  setCampaignStatus(id: string, data: object) {
    return this.put(`/super-admin/campaign/${id}/status`, data);
  }

  transferBusinessOwnership(
    businessId: string,
    mail: string,
    name: string,
    removePreviousOwner: boolean
  ) {
    return this.post('/super-admin/owner-invite', {
      businessId,
      mail,
      name,
      removePreviousOwner
    });
  }

  deleteManagerInvite(inviteId: string) {
    return this.delete(`/super-admin/owner-invite/${inviteId}`);
  }

  sendWebhookFakeFidel(payload: FormWebhookFakeFidel) {
    return this.post('/super-admin/transaction/webhook-fake/fidel', payload);
  }

  sendManuallyReward(payload: FormManuallyReward) {
    return this.post('/super-admin/lambda-webhooks/manually-reward', payload);
  }

  updateOffer(
    offerId: string,
    data: {
      title?: string;
      imageId?: string;
      description?: string;
      restrictions?: string;
      discountTagValue?: string;
      setActive?: boolean;
    }
  ) {
    return this.put(`/super-admin/offer/${offerId}`, data);
  }

  triggerTransactionsVerification(payload: any) {
    return this.post(
      '/lambda-webhooks/transaction/check-pending-coins',
      payload
    );
  }

  sendManuallyEventToSegment({
    name,
    timestamp,
    userId,
    metadata,
    platform
  }: {
    name: string;
    timestamp?: string;
    userId: string;
    metadata: any;
    platform: string;
  }) {
    return this.post(`/super-admin/event-tracker/track`, {
      name,
      userId,
      timestamp,
      metadata,
      platform
    });
  }

  getIdentifyCustomersSettings() {
    return this.get('/super-admin/event-tracker/identify-customers');
  }

  runIdentifyCustomersScript() {
    return this.post('/super-admin/event-tracker/identify-customers');
  }
}
